/*
 * Copyright (c) 2020 CalAmp Corp.  All Rights Reserved
 */
import React, {
  memo,
  MutableRefObject,
  useCallback,
  useEffect,
  useRef,
  useState
} from 'react';
import {loadModules} from 'esri-loader';
import debounce from 'lodash.debounce';
import isNil from 'lodash.isnil';
import Supercluster from 'supercluster';
import {addSelectedAsset, removeSelectedAsset} from '@ion/core/src/entities/Asset';
import {error} from '@ion/core/src/logger/logger';
import {isCVSAsset} from '@ion/core/src/services/ArcGISUtils';
import Device from '@ion/core/src/services/Device';
import {GlobalSearchResult} from '@ion/core/src/services/GlobalSearch';
import {
  AugmentedSmarterTrailerSensorData,
  Crumb,
  CVSAssetAggregations,
  CVSAssetType,
  GenericObject,
  HVSRecord,
  LatLong,
  SCDeviceType,
  SensorTypes,
  TagReportType,
  ToolAssetType
} from '@ion/core/src/types/entities';
import {idMap} from '@ion/core/src/utils/ObjectUtil';
import useRootManager from '../../client/useRootManager';
import {useMedia} from '../../hooks/useMedia';
import {
  BaseMapEnum,
  BoundaryFilterType,
  ColorModeEnum,
  HistoryInputEvents,
  HistoryReportTypeEnum,
  TagArchitecture
} from '../../types/types';
import {ASSET_LAYER_FIELDS, mapViewGoToOptions} from '../constants';
import {initializeEsriModules} from '../helpers/initializeEsriModules';
import {getLabelClassQuery, getLayerLabelClass} from '../helpers/LabelUtils';
import {
  applyEditsToLayer,
  createGraphics,
  getAssetLayerRenderer,
  getAssetsAggregationLevel,
  getFeatures as getAssetEntity,
  getItemQuery,
  getItemsUpdates,
  getLatestUpdate,
  getLayerByTitle,
  getLayerEffectQuery,
  getMapviewZoomParams,
  getMapZoomLevel,
  getTrackingAssets,
  getUtilizationColor,
  hasAssetUpdated,
  layerExists,
  noneSelectedQuery,
  onLayerClickEvent,
  shouldZoomTo
} from '../helpers/MapViewUtils';
import {useAddUpdateItems} from '../hooks/useAddUpdateItems';
import {useFeatureLayerEffect} from '../hooks/useAssetFeatureLayerEffect';
import useAssetMapping from '../hooks/useAssetMapping';
import useClusterExtent from '../hooks/useClusterExtent';
import useLayerAutoUpdate from '../hooks/useLayerAutoUpdate';
import useLayerSelection from '../hooks/useLayerSelection';
import {useLocationMarker} from '../hooks/useLocationMarker';
import {usePanToAsset} from '../hooks/usePanToAsset';
import useZoomTo from '../hooks/useZoomTo';
import {
  createMarkers,
  LabelContentClient,
  MARKER_MODE_BREAKPOINT,
  MarkerMode
} from '../markers/AssetMarkers';
import {isCurrentEnabled} from './LayerChooser';

/**
 * Defines the unique identifier for the assets layer which is used as globally
 * as the layer id.
 */
export const ASSET_LAYER_ID   = 'assets';
export const CLUSTER_LAYER_ID = 'clusterLayer';

// Title of the CVS Layer from the WebMap
const WEBMAP_CVS_TITLE = 'CVS';
// Default Interval of CVS Layer auto update (12 seconds)
const DEFAULT_INTERVAL = 12000;

/**
 * Defines the time to determine if the aggregation data is still valid
 * this is value in Minutes
 */
export const AGGREGATION_TIME = 10;

/**
 * Defines the default aggregation level, on this level, it should keep
 * query the service
 */
export const DEFAULT_AGGREGATION_LEVEL = 'clusters';

/**
 * Cluster radius value, in pixels.
 */
export const CLUSTER_RADIUS = 40;
/**
 * Minimum zoom level at which clusters are generated
 */
export const CLUSTER_MINZOOM = 1;

/**
 * Maximum zoom level at which clusters are generated.
 */
export const CLUSTER_MAXZOOM = 22;

/**
 * Default expand factor.
 * The value of 1.10 means expand any extent by 10% more.
 * https://developers.arcgis.com/javascript/latest/api-reference/esri-geometry-Extent.html#expand
 */
export const EXTENT_FACTOR = 1.10;

/**
 * Default View Zoom
 */
export const VIEW_ZOOM = 17;

/**
 * Max Cluster Aggregation Level [Countries and states]
 */
export const MAX_AGGREGATION_LEVEL = 6;

// Defines the identifier property for Asset layer
const identifierProperty = 'assetId';

// Defines the identifier type for Asset layer
const identifierType = 'isCvsAsset';

export type FindNearbyType = {
  isActive: boolean;
  isLoading: boolean;
  asset: CVSAssetType | null;
  geofence: any;
  location: any;
  results: Array<CVSAssetType> | [];
  error: any;
  label: string;
  miles: number;
  type: any;
}

// defines the asset layer prop definitions ...
export type AssetLayerProps = {
  assets?: Device[];
  assetsAggregation?: CVSAssetAggregations[];
  assetsUpdates?: Device[];
  assetsLoading?: boolean;
  assetsCount?: number;
  history?: HVSRecord[];
  assetsHistory?: HVSRecord[];
  lastPositionHistory?: CVSAssetType[];
  selectedHistoryAsset?: CVSAssetType;
  user: any;
  crumbs?: Crumb[];
  setCrumbs?: Function;
  showSidesheetAssetDetail?: Function;
  setLoaderIndicator?: Function;
  setSelectedAsset?: Function;
  setSelectedDevice?: Function;
  setHistorySelectedAsset?: Function;
  setAssetDetailView?: Function;
  setCloseAssetDetailView?: any;
  assetDetailView?: boolean;
  setDeviceDetailView?: Function;
  setIONVision: Function;
  hasIONVision?: boolean;
  selectedAsset?: CVSAssetType;
  selectedDevice?: SCDeviceType;
  onAssetSelected: Function;
  onDeviceSelected?: Function;
  isMapSelection?: boolean;
  isClusteringActive?: boolean;
  isEnabled?: boolean;
  onHoveredPanelAsset?: Function;
  selectedTool?: ToolAssetType;
  toolDetailView?: boolean;
  hoveredAsset?: CVSAssetType;
  hoveredEvent?: HVSRecord | null,
  onEventHovered?: (event: HVSRecord | null) => void;
  disconnectedToolTagReport?: {
    tagReport: TagReportType;
  };
  findNearbyResults: null | Device[];
  resumeMapStatus?: boolean,
  basemap?: BaseMapEnum;
  toggleResumeMap?: Function,
  panelBreadcrumbs?: Crumb[],
  onClearSearchedAsset?: Function,
  searchResult?: GlobalSearchResult | null,
  onAssetsUpdates: Function,
  setIsAutoRefresh: Function,
  setFindButtonStatus: Function,
  isAutoRefresh: boolean,
  historyUpdatesLive?: boolean,
  isHistoryRefresh?: boolean,
  historyReportType: HistoryReportTypeEnum,
  visible?: boolean
  isLargeFleetMode?: boolean,
  layerFields?: string[],
  popupTemplate?: any,
  renderer?: any,
  createEventLineLayerData?: Function,
  historyIntervalInHours?: number,
  historyInputEvents?: HistoryInputEvents[],
  createLineRenderer?: Function,
  setHistoryRefresh?: Function,
  selectedAssetTrailer?: AugmentedSmarterTrailerSensorData,
  smartTrailerLoading?: boolean,
  assetViewHistoryClick?: Function,
  assetFindNearbyClick?: Function
  routeHistory?: any;
  getAlignedRoutes?: Function,
  isFindNearbyPopup?: boolean,
  isFindNearbyToggle?: boolean,
  onFindNearyBy?: Function,
  onFindNearbyPopupStatusChange?: Function,
  onFindNearbyBreadcrumbBackClick?: Function,
  onFindButtonStateChange?: Function,
  findLabel?: FindNearbyType,
  findNearbyIsActive?: boolean,
  findButtonState?: boolean,
  devicesHaveTrailerSensors: {
    esn: string;
    apps: SensorTypes[];
  }[],
  smarterTrailerError: Error,
  globalGroupId: number,
  scDevices?: Device[],
  scDevicesUpdates?: Device[],
  loadSCDevices?: Function,
  loadSCDevicesUpdates?: Function,
  onResetSCDevices?: Function,
  hasGridUpdate?: boolean,
  setHasGridUpdate?: Function,
  loadAssetHistory?: Function,
  setHistoryLiveUpdates?: Function;
  searchedResultRef?: any;
  onBoundaryFilterChange?: Function;
  currentLocationRef?: MutableRefObject<LatLong>;
  showAssetsList?: boolean;
  isPreferenceRefresh?: boolean;
  isScDevicesLabelActive?: boolean;
  onDeviceReports?: Function;
  scDevicesLabelContent?: string;
  boundary?: BoundaryFilterType;
} & LabelContentClient;

export type UpdaterProps = {
  graphic: __esri.Graphic,
  view?: __esri.MapView,
  asset: CVSAssetType,
  isHover: boolean,
  mode?: MarkerMode,
  onHoveredPanelAsset?: Function,
  hoveredAsset?: CVSAssetType,
  markerOnly?: boolean
} & LabelContentClient;

// defines the prop definitions for the AssetLayer
type Props = {
  map?: __esri.Map;
  view?: __esri.MapView;
  assetLayerRef?: any;
  initialBasemap?: string;
  toggledBaseMap?: boolean,
  onBaseMapChangeClick?: Function,
  setOmitCvsCall?: Function,
  color?: ColorModeEnum;
  layers?: string[];
  assetLayerProps?: AssetLayerProps;
  popupElement?: any;
};

// Defines asset Features props
type AssetFeaturesProps = {
  assets: CVSAssetType[],
  isClientCluster: boolean,
  isClusteringActive: boolean,
  currentSelectedAsset: any
}

// Defines label class props
type LabelClassProps = {
  labelContent: string,
  basemap: BaseMapEnum,
  selectedAsset: CVSAssetType,
  isCVSRenderer: boolean
}

const canDisplay = (layers: string[]) => {
  return isCurrentEnabled(layers);
};

/**
 * Provides a utility which returns the current marker mode based on the map views
 * current zoom level.
 */
const getMarkerMode = ({zoom}) => {
  return MarkerMode.detailed;
};

/**
 * Provides a utility which determines if the asset markers require re-rendering based
 * on the current and previous zoom states.
 */
export const shouldRerender = (current: number, previous: number) => {
  const bp = MARKER_MODE_BREAKPOINT;
  if ((previous >= bp && current < bp) || (previous < bp && current >= bp)) {
    return true;
  }
  return false;
};

/**
 * Provides a utility to get the Cluster Points based on BoundingBox and Map Zoom level.
 */
export const getClusterPoints = (superclusterRef, {zoom}) => {
  try {
    // Passing Default extent values for full map view
    return superclusterRef.current.getClusters(
      [-180, -85, 180, 85],
      Math.round(zoom)
    );
  } catch (err) {
    error(err);
    return [];
  }
};

// Defines a utlitity to return array of assets with geometry
export const getAssetPoints = assets => {
  const points = [];
  assets?.forEach(asset => {
    const item = getAssetEntity(asset);
    if (item?.OBJECTID) {
      points.push({
        ...item,
        geometry : {
          type        : 'Point',
          coordinates : [item?.longitude, item?.latitude]
        }
      });
    }
  });
  return points;
};

/**
 * Defines a utility to return the selected asset or null based on validating
 * if it is asset
 */
const getSelectedAsset = (selectedAsset: CVSAssetType) => {
  return isCVSAsset(selectedAsset) ? selectedAsset : null;
};

// Defines a utility to return single assets
const getSingleAssets = (assets: any[]) => {
  return assets?.length ? assets
    .reduce((items, item) => {
      if (!item?.type) {
        items.push({...item, utilizationColor: getUtilizationColor(item)});
      }
      return items;
    }, []) : [];
};

// Defines a utility to return Clusters
const getClusterAssets = (assets: any[]) => {
  return assets?.length ? assets.filter(item => item?.type) : [];
};

// Returns a query based on assetids
const getAssetIdQuery = (assets: Device[]) => {
  return `assetId IN (${assets?.map(asset => asset?.getDevice()?.assetId)})`;
};

// Returns the Layer object
const getLayer = (layer: GenericObject) => layer?.current;

/**
 * @AssetLayer
 *
 * Defines the AssetLayer which manages rendering a layer containing individual
 * Assets within a DLS Map.
 *
 * ```typescript
 *
 *   import {ResponsiveBaseMapLayer, AssetLayer} from './layers';
 *   const components: any = [ResponsiveBaseMapLayer]
 *   components.push(AssetLayer);
 *
 * ```
 *
 */
export const AssetLayer = ({
  view,
  map,
  layers,
  initialBasemap,
  popupElement,
  assetLayerProps: {
    assets: assetsNormalFleet,
    assetsAggregation: assetsLargeFleet,
    assetsUpdates,
    assetsLoading,
    assetsCount,
    showSidesheetAssetDetail,
    setLoaderIndicator,
    onAssetSelected,
    onHoveredPanelAsset,
    selectedAsset,
    isMapSelection,
    selectedTool,
    toolDetailView,
    disconnectedToolTagReport,
    findNearbyResults,
    toggleResumeMap,
    searchResult,
    onClearSearchedAsset,
    assetDetailView,
    onAssetsUpdates,
    isAutoRefresh,
    setIsAutoRefresh,
    isClusteringActive,
    isLargeFleetMode,
    isLabellingActive,
    labelContent,
    defaultlabel,
    globalGroupId,
    isFindNearbyPopup,
    isFindNearbyToggle,
    onFindNearbyPopupStatusChange,
    findNearbyIsActive,
    findLabel,
    hasGridUpdate,
    setHasGridUpdate,
    searchedResultRef,
    currentLocationRef,
    showAssetsList,
    basemap,
    boundary
  } = {} as AssetLayerProps,
  color = ColorModeEnum.dark
}: Props) => {
  const [selectedPopupGraphic, setSelectedPopupGraphic]     = useState(null);
  const [renderMarkerWhenResume, setRenderMarkerWhenResume] = useState(false);
  const [previousSelectedAsset, setPreviousSelectedAsset]   = useState(null);
  const [mapMarkers, setMapMarkers]                         = useState(null);
  const [viewReady, setViewReady]                           = useState(false);
  const {render, createContainer}                           = useRootManager();
  const [assets, setAssets]                                 = useState(
    getTrackingAssets({
      assetsNormalFleet,
      assetsLargeFleet,
      viewZoom     : getMapZoomLevel(view.zoom, initialBasemap, isLargeFleetMode),
      isLargeFleet : isLargeFleetMode
    })
  );

  const viewRef               = useRef(view);
  const singleLayerRef        = useRef(null);
  const clusterLayerRef       = useRef(null);
  const graphicRef            = useRef(null);
  const superclusterRef       = useRef(null);
  const currentPopupGraphic   = useRef(null);
  const isFullExtendRequired  = useRef(true);
  const zoomWatchRef          = useRef(null);
  const panWatchRef           = useRef(null);
  const popupContentNodeRef   = useRef(null);
  const viewClickRef          = useRef(null);
  const assetsLoadingRef      = useRef(assetsLoading);
  const globalGroupIdRef      = useRef(globalGroupId);
  const hasGroupChangedRef    = useRef(false);
  const labelRef              = useRef({isLabellingActive, labelContent, defaultlabel});
  const zoomRef               = useRef(view.zoom);
  const layersRef             = useRef(layers);
  const assetsRef             = useRef(assets);
  const assetsNormalFleetRef  = useRef(assetsNormalFleet);
  const assetsLargeFleetRef   = useRef(assetsLargeFleet);
  const isLargeFleetModeRef   = useRef(isLargeFleetMode);
  const isClusteringActiveRef = useRef(isClusteringActive);
  const selectedAssetRef      = useRef(selectedAsset);
  const colorRef              = useRef(color);
  const findNearbyPopupActive = useRef(false);
  const isFindNearbyPopupRef  = useRef(isFindNearbyPopup);
  const findNearbyIsActiveRef = useRef(findNearbyIsActive);
  const {isPhone}             = useMedia();
  const isPhoneRef            = useRef(isPhone);
  const selectedItemRef       = useRef(selectedAsset);
  const zoomTo                = useZoomTo();
  const getAssetMapping       = useAssetMapping();
  const getClusterExtent      = useClusterExtent();
  const [addMarker]           = useLocationMarker(view, map, true);
  const panToAsset            = usePanToAsset(view, selectedAsset);
  const addUpdateItems        = useAddUpdateItems();
  const findNearbyLabelRef    = useRef(findLabel);
  const refreshLayerRef       = useRef(false);
  const {
    createSelectionEffect,
    setLayerHighlighter,
    removeHighlight,
    initializePopup,
    highlightItem
  } = useFeatureLayerEffect();

  useEffect(function() {
    !isPhoneRef.current && initializePopup(view);
  }, [view]);

  const {
    timerUpdateRef,
    updateIntervalRef,
    startUpdate,
    stopUpdate
  } = useLayerAutoUpdate({
    layers       : map?.layers as any,
    title        : WEBMAP_CVS_TITLE,
    timeInterval : DEFAULT_INTERVAL,
    callback     : () => getItemsUpdates(assetsRef.current, 'dbWriteTime', onAssetsUpdates)
  });
  const cvsLayer      = getLayerByTitle(map?.layers as any, WEBMAP_CVS_TITLE);
  const isCVSRenderer = !isNil(cvsLayer?.renderer);

  useEffect(() => {
    if (findNearbyIsActive) {
      stopAssetsAutoRefresh();
    } else if (findNearbyIsActiveRef.current && !findNearbyIsActive) {
      refreshLayerRef.current = true;
    }
    findNearbyIsActiveRef.current = findNearbyIsActive;
  }, [findNearbyIsActive]);

  useEffect(() => {
    findNearbyLabelRef.current = findLabel;
  }, [findLabel]);

  useEffect(debounce(() => {
    const assetLength = assetsNormalFleet?.length;
    if (assetLength && !isLargeFleetModeRef.current &&
      !findNearbyIsActiveRef.current && !selectedAssetRef.current) {
      if (!boundary?.length) {
        const points = getAssetPoints(assetsNormalFleet);
        const extent = zoomTo(points, EXTENT_FACTOR);
        const pointCount = points.length;
        const zoomParams = getMapviewZoomParams(extent, pointCount, VIEW_ZOOM);
        zoomParams && viewReady && view.goTo(zoomParams, mapViewGoToOptions);
      }
    }
  }, 200), [assetsNormalFleet, findNearbyIsActive]);

  /**
   * Watch for selected asset from list or grid and highlight in Feature Layer
   */
  useEffect(() => {
    removeHighlight(singleLayerRef?.current);
    !findNearbyIsActive && layerAssetHighlight(selectedAsset);
  }, [selectedAsset, findNearbyIsActive]);

  // Update label class based on label content
  useEffect(() => {
    const singleLayer       = getLayer(singleLayerRef);
    selectedItemRef.current = selectedAsset;
    if (singleLayer) {
      const selectedAssetLabel = !findNearbyIsActive ? selectedAsset : null;
      singleLayer.labelingInfo = getLabelClass({
        selectedAsset : selectedAssetLabel as any,
        labelContent,
        isCVSRenderer,
        basemap
      });
    }
  }, [selectedAsset, labelContent, basemap, findNearbyIsActive]);

  // Label visibility
  useEffect(() => {
    const singleLayer = getLayer(singleLayerRef);
    if (singleLayer) {
      singleLayer.labelsVisible = isLabellingActive;
    }
  }, [isLabellingActive]);

  // Update Assets
  useEffect(() => {
    if (assetsUpdates?.length && !isLargeFleetModeRef.current) {
      updateLocalState(assetsUpdates);
      if (!isClusteringActiveRef.current) {
        updateSingleAssets(assetsUpdates);
      } else {
        updateSinglesAndClusters(assetsUpdates, runClusterProccess);
      }
    }
  }, [assetsUpdates]);

  const setAssetHighlight = useCallback((assets: any) => {
    if (assets?.length && selectedAssetRef.current) {
      const selected = assets.map(getAssetEntity)?.find(({assetId}) =>
        assetId === selectedAssetRef.current?.assetId);
      selected && layerAssetHighlight(selected);
    }
  }, []);

  const layerAssetHighlight = useCallback(debounce((asset: CVSAssetType) => {
    const layerRef = singleLayerRef?.current;
    if (layerRef) {
      if (asset && !findNearbyIsActiveRef.current) {
        const query = getItemQuery({
          item         : asset,
          property     : identifierProperty,
          defaultQuery : noneSelectedQuery
        });
        const params = {
          selectedItem : asset,
          type         : identifierType,
          query
        };
        if (isLargeFleetModeRef.current) {
          params.defaultQuery = query;
        }
        const where = getLayerEffectQuery(params);
        highlightItem(layerRef, where);
        createSelectionEffect(layerRef, where);
      } else if (layerRef?.featureEffect) {
        layerRef.featureEffect = null;
      }
    }
  }, 135), []);

  const getLabelClass = useCallback(({
    labelContent,
    selectedAsset,
    isCVSRenderer,
    basemap
  }: LabelClassProps) => {
    const where = getLabelClassQuery({
      selectedItem : selectedAsset,
      type         : identifierType,
      property     : identifierProperty
    });
    const labelClass = getLayerLabelClass({
      isRenderer   : isCVSRenderer,
      defaultColor : true,
      labelContent,
      basemap,
      where
    });
    return labelClass;
  }, []);

  const showPopup = useCallback(({attributes}) => {
    if (attributes && attributes.type !== 'Feature' && !isPhoneRef.current) {
      isLargeFleetModeRef.current && showSidesheetAssetDetail?.(true, attributes, true);
      const {longitude, latitude} = attributes;
      const AssetPopupComponent   = popupElement;
      const popupContainer        = createContainer('asset-popup-container');
      render(<AssetPopupComponent asset={attributes} mapView={view}/>, popupContainer);
      view.popup.open({
        location : {
          type : 'point',
          longitude,
          latitude
        },
        content : popupContainer
      });
    }
  }, [popupElement, view]);

  // Create the Asset Layer
  const createAssetLayer = debounce(async(assets: Device[]) => {
    if (!singleLayerRef.current) {
      if (!layerExists(map, ASSET_LAYER_ID)) {
        const {layers}       = map;
        const [FeatureLayer] = await initializeEsriModules(
          'esri/layers/FeatureLayer');
        const graphics       = await createGraphics(assets, ASSET_LAYER_FIELDS);
        if (graphics?.length) {
          singleLayerRef.current = new FeatureLayer({
            title         : ASSET_LAYER_ID,
            id            : ASSET_LAYER_ID,
            outFields     : ['*'],
            source        : graphics,
            objectIdField : 'OBJECTID',
            renderer      : cvsLayer?.renderer || getAssetLayerRenderer(),
            fields        : ASSET_LAYER_FIELDS,
            labelingInfo  : getLabelClass({
              selectedAsset : selectedItemRef.current,
              labelContent,
              isCVSRenderer,
              basemap
            }),
            popupEnabled : false
          });
          layers.push(singleLayerRef.current);
          setLayerHighlighter(singleLayerRef.current, view);
          singleLayerRef.current.visible = isCurrentEnabled(layersRef.current);
          selectedItemRef.current && layerAssetHighlight(selectedItemRef.current);
        }
      }
    }
  }, 250);

  // Returns assets features
  const getAssetFeatures = useCallback(async({
    assets,
    isClientCluster,
    isClusteringActive,
    currentSelectedAsset
  }: AssetFeaturesProps) => {
    let items     = [];
    let features  = [];
    let points    = [];
    if (isClientCluster) {
      points = getAssetPoints(assets);
      if (isClusteringActive) {
        superclusterRef.current.load(removeSelectedAsset(points, currentSelectedAsset) || []);
        items = getClusterPoints(superclusterRef, view);
      }
      features = getFeatures(isClusteringActive, items, points);
    } else {
      features = getAssetMapping(assets, view.spatialReference);
    }
    if (currentSelectedAsset) {
      features = addSelectedAsset({
        items         : features,
        assets        : points,
        selectedAsset : getAssetPoints([currentSelectedAsset])[0],
        include       : !isClientCluster
      });
    }
    return {features, points};
  }, [view]);

  // Update single assets
  const updateSingleAssets = useCallback(async(updates: Device[]) => {
    if (singleLayerRef.current) {
      updateSingleLayer(
        updates,
        getAssetIdQuery(updates),
        () => onSingleLayerUpdates(updates)
      );
    }
  }, []);

  // Update singles and clusters
  const updateSinglesAndClusters = useCallback(async(
    updates: Device[],
    callback: Function
  ) => {
    singleLayerRef.current && updateSingleLayer([], getAssetIdQuery(updates), callback);
  }, []);

  // Add and Remove feature from the layer
  const updateSingleLayer = useCallback(async(
    assets: Device[],
    where: string,
    callback?: Function
  ) => {
    const graphics = await createGraphics(assets, ASSET_LAYER_FIELDS);
    const query    = singleLayerRef?.current?.createQuery();
    query.where    = where;
    singleLayerRef.current?.queryFeatures(query).then(({features}) => {
      applyEditsToLayer(
        singleLayerRef.current,
        {deleteFeatures: features, addFeatures: graphics},
        callback
      );
    });
  }, []);

  // Add / Remove Assets
  const addRemoveAssets = useCallback(async(assets: Device[], selectedAsset: CVSAssetType) => {
    if (singleLayerRef.current) {
      if (!isLargeFleetModeRef.current) {
        singleLayerRef.current?.queryFeatures().then(({features}) => {
          let selectedAssetId = null;
          // Verify if the selected asset updated
          if (selectedAsset) {
            const current = features?.find(({attributes}) =>
              attributes.assetId === selectedAsset.assetId);
            if (selectedAsset.eventTime > current?.attributes?.eventTime) {
              selectedAssetId = selectedAsset.assetId;
            }
          }
          // Singles AssetIds from the singleLayer;
          const featuresIds = features?.map(({attributes}) => attributes.assetId);
          // Singles AssetIds after the re-clustering process
          const updatesIds  = idMap(assets, identifierProperty);
          // Assets to remove - these exist in the singleLayer but these are not
          // single assets anymore (After the re-clustering process)
          const featuresToRemove = features?.filter(({attributes}) =>
            !updatesIds[attributes.assetId] || attributes.assetId === selectedAssetId);
          // Assets to Insert - these are new single-assets (after re-clustering process)
          // and these are not exist in the singleLayer
          const featuresToInsert = assets?.filter(({assetId}) =>
            !featuresIds?.includes(assetId) || assetId === selectedAssetId);
          // Apply changes
          applyChangesToSingleLayer(featuresToRemove, featuresToInsert);
        });
      } else {
        updateSingleLayer(assets, '1 = 1', () => onSingleLayerUpdates(assets));
      }
    }
  }, []);

  // Apply changes to single layer
  const applyChangesToSingleLayer = useCallback(async(
    toRemove: __esri.Feature[],
    toInsert: Device[]
  ) => {
    const addFeatures = await createGraphics(toInsert, ASSET_LAYER_FIELDS);
    applyEditsToLayer(
      singleLayerRef.current,
      {deleteFeatures: toRemove, addFeatures},
      () => onSingleLayerUpdates(toInsert)
    );
  }, []);

  // Re-run the clustering process
  const runClusterProccess = useCallback(() => {
    clusterFeatures(null, isClusteringActiveRef.current);
  }, []);

  // Set single layer visibility and highlight selected asset
  const onSingleLayerUpdates = useCallback(debounce((assets: any) => {
    setSingleLayerVisibility();
    setAssetHighlight(assets);
  }, 200), []);

  // Update the local states
  const updateLocalState = useCallback((updates: Device[]) => {
    const normalized = updates?.map(item => item?.getDevice?.()) || [];
    const assetIdMap = idMap(normalized, identifierProperty);
    const current    = assetsRef.current?.filter(({assetId}) => !assetIdMap[assetId]) || [];
    setAssets([...current, ...normalized]);
  }, []);

  // Defines a utility to return a feature based params
  const getFeatureByValue = useCallback(async(
    layer: __esri.FeatureLayer,
    field: string,
    value: number
  ) => {
    if (layer) {
      const query = layer.createQuery();
      query.where = `(${field} = ${value})`;
      const feature = await layer.queryFeatures(query).then(({features}) => {
        return features?.[0] || null;
      });
      return feature;
    }
    return null;
  }, []);

  // upon initialization of the asset layer, the graphics layer is loaded as well as
  // the graphic class and the layers underlying graphics layer is initialized ...
  useEffect(() => {
    loadModules([
      'esri/layers/GraphicsLayer',
      'esri/Graphic'
    ])
      .then(([GraphicsLayer, Graphic]) => {
        async function loadAssets() {
          const currentSelectedAsset = getSelectedAsset(selectedAssetRef.current);
          const isClientCluster      = isClientSideCluster(isLargeFleetMode);

          if ((isLargeFleetMode || (isAutoRefresh && !hasGridUpdate)) && viewReady) {
            isAutoRefresh && setIsAutoRefresh(false);
            return;
          }
          if (clusterLayerRef.current) {
            removeAllGraphics();
          } else {
            clusterLayerRef.current = new GraphicsLayer({
              id    : CLUSTER_LAYER_ID,
              title : CLUSTER_LAYER_ID
            });
            map.layers.push(clusterLayerRef.current);
            view.popup.watch('visible', function() {
              // With an outside click, ArcGIS sets popup.visible to false
              // Check that asset popup has a current reference
              // If so, update marker to include label
              if (!view.popup.visible && currentPopupGraphic.current) {
                currentPopupGraphic.current = null;
                setSelectedPopupGraphic(null);
                onFindNearbyPopupStatusChange(false);
              }
            });
            initializeSelection();
          }
          if (!superclusterRef.current) {
            superclusterRef.current = new Supercluster({
              radius  : CLUSTER_RADIUS,
              minZoom : CLUSTER_MINZOOM,
              maxZoom : CLUSTER_MAXZOOM
            });
          }
          const {features} = await getAssetFeatures({
            assets,
            isClientCluster,
            isClusteringActive,
            currentSelectedAsset
          });

          /*if the user selects a tool from the side panel we need
           * to add that tool to the graphics list for this layer so that
           * the asset marker will display and we need to zoom into that point.
           */
          const {toolDetails} = validateSelectedTool(features);
          const singleAssets  = getSingleAssets(features);
          const clusterAssets = getClusterAssets(features);

          // Start - add Single Layer
          const params = {
            layer        : singleLayerRef.current,
            data         : singleAssets,
            create       : createAssetLayer,
            addUpdate    : addRemoveAssets,
            selectedItem : currentSelectedAsset
          };
          addUpdateItems(params);
          // End - add Single Layer

          const markers = createMarkers({
            assets        : clusterAssets,
            mode          : getMarkerMode(view),
            selectedAsset : currentSelectedAsset,
            markerOnly    : !isNil(selectedPopupGraphic || currentPopupGraphic?.current),
            Graphic,
            color,
            isLabellingActive,
            labelContent,
            defaultlabel
          }) || [];
          clusterLayerRef.current.visible = canDisplay(layers);
          graphicRef.current = Graphic;
          if (toolDetailView) {
            if (toolDetails?.tagReport) {
              view.zoom = VIEW_ZOOM;
              view.goTo({
                center : [toolDetails.tagReport?.longitude, toolDetails.tagReport?.latitude]
              }, mapViewGoToOptions).then(() => {
                addGraphics(markers);
                toggleResumeMap(false);
              }).catch(err => error(err));
            } else {
              addGraphics(markers);
            }
          } else {
            if (!clusterAssets?.length && !singleAssets?.length && !isLargeFleetMode) {
              !assetsCount && handleTrackingLoading(false);
              return;
            }
            if (canDisplay(layers) && showAssetsList && !selectedAsset) {
              if (isFullExtendRequired.current) {
                toggleResumeMap(false);
                setViewReady(true);
                setMapMarkers(markers);
                setRenderMarkerWhenResume(true);
                viewReady && addGraphics(markers);
              } else {
                addGraphics(markers);
                if (toolDetailView) {
                  view.zoom = VIEW_ZOOM;
                  view.goTo({
                    center : [toolDetails.tagReport?.longitude, toolDetails.tagReport?.latitude]
                  });
                }
              }
            } else {
              if (canDisplay(layers) && currentSelectedAsset &&
                  !findNearbyIsActiveRef.current && !isAutoRefresh) {
                /**
                 * Deeplink: To ensure the marker is rendered before focusing on map.
                 * Otherwise the map will point to random location.
                 */
                setTimeout(() => {
                  const {longitude, latitude} = currentSelectedAsset;
                  view.zoom = isLargeFleetMode ? VIEW_ZOOM + 1 : VIEW_ZOOM;
                  view.goTo({
                    center : [longitude, latitude]
                  }, mapViewGoToOptions).then(() => {
                    setViewReady(true);
                    markers?.length && addGraphics(markers);
                    setMapMarkers(markers);
                  }).catch(err => error(err));
                }, 800);
              } else {
                setViewReady(true);
                markers?.length && addGraphics(markers);
                setMapMarkers(markers);
              }
            }
          }
          setViewReady(true);
          viewClickRef.current = view.on('click', viewClickHandler);
          onAssetsUpdates && startAssetsAutoRefresh();
        }
        loadAssets();
        setHasGridUpdate(false);
      })
      .catch((err) => {
        error(err);
      });
    return function() {
      view.graphics.removeAll();
      addBackGraphics();
    };
  }, [view, toolDetailView, assets, isLargeFleetMode]);

  useEffect(() => {
    return () => {
      stopAssetsAutoRefresh();
      popupContentNodeRef.current = null;
      removeHighlight(singleLayerRef?.current);
    };
  }, []);

  /**
   * Utility to return clusters or events based on the Clustering visibility
   */
  const getFeatures = useCallback((isClusteringActive: boolean, clusters: any[], events: any[]): any[] => {
    return isClusteringActive ? clusters : events;
  }, []);

  /**
   * Utility to validate the clustering process on client or backend mode
   */
  const isClientSideCluster = useCallback((isLargeFleet: boolean): boolean => {
    return !isLargeFleet;
  }, [view.zoom]);

  useEffect(() => {
    const hasChanged    = hasAssetUpdated(previousSelectedAsset, getSelectedAsset(selectedAsset));
    const isAllowByMode = (isLargeFleetModeRef.current && !isMapSelection) || !isLargeFleetModeRef.current;
    if (hasChanged && isAllowByMode) {
      resetPopupValues();
      panToAsset();
    }
  }, [selectedAsset, isMapSelection]);

  const resetPopupValues = useCallback(() => {
    if (view.popup.visible && currentPopupGraphic.current) {
      setSelectedPopupGraphic(null);
      currentPopupGraphic.current = null;
      view.popup.close();
    }
  }, [view.popup]);

  /**
   * addGraphics method allows adding graphics to the CVS Layer
   */
  const addGraphics = useCallback(graphics => {
    if (graphics?.length) {
      clusterLayerRef.current?.graphics?.addMany(graphics);
    }
  }, []);

  /**
   * addBackGraphics method handles adding back a search or current location graphic after clearing asset graphics
   */
  const addBackGraphics = useCallback(() => {
    if (searchedResultRef?.current) {
      view.graphics.add(searchedResultRef.current);
    }
    if (currentLocationRef?.current) {
      const {latitude, longitude} = currentLocationRef.current;
      addMarker(latitude, longitude, '');
    }
  }, [searchedResultRef?.current, currentLocationRef?.current]);

  /**
   * removeAllGraphics method allows removing all the graphics from the CVS Layer
   */
  const removeAllGraphics = useCallback(() => {
    clusterLayerRef.current?.graphics?.removeAll();
    addBackGraphics();
  }, []);

  /**
   * handleTrackingLoading method allows setting tracking loading status
   */
  const handleTrackingLoading = useCallback(debounce(value => {
    setLoaderIndicator(value);
  }, 300), [setLoaderIndicator]);

  /**
   * updateAssets function helps to update the Asset Layer Features
   * and auto-refresh
   */
  const updateAssets = useCallback((layers: string[], isClusteringActive: boolean) => {
    if (clusterLayerRef.current) {
      const isLayerVisible = isCurrentEnabled(layers);
      clusterLayerRef.current.visible = isLayerVisible;
      isLayerVisible && clusterFeatures(null, isClusteringActive);
      startAssetsAutoRefresh();
    }
  }, []);

  const showGraphicPopup = useCallback(selectedGraphic => {
    setSelectedPopupGraphic(selectedGraphic);
  }, []);

  const clusterSelected = useCallback(debounce(selectedClusterId => {
    try {
      let assetExtent    = null;
      let zoomParams     = null;
      let isClusterOfOne = null;
      if (!isLargeFleetModeRef.current) {
        assetExtent = superclusterRef.current
          .getLeaves(selectedClusterId, Infinity)
          .map(({geometry}) => geometry?.coordinates);
      } else {
        const {graphics} = clusterLayerRef.current;
        const asset = graphics?.items?.filter(({attributes}) => attributes?.id === selectedClusterId);
        if (asset?.length) {
          const {attributes} = asset[0];
          assetExtent    = getClusterExtent(attributes?.extent, view.spatialReference);
          isClusterOfOne = attributes?.properties?.point_count === 1;
        }
      }
      const extent = isLargeFleetModeRef.current
        ? assetExtent?.expand(EXTENT_FACTOR)
        : zoomTo(assetExtent, EXTENT_FACTOR);

      if (shouldAddZoomLevel(isLargeFleetModeRef.current, extent) || isClusterOfOne) {
        /* When cluster of one asset is clicked, center and zoom to a default zoom level,
         * otherwise zoom in one more level.
         */
        const zoom = !isClusterOfOne ? view.zoom + 1 : VIEW_ZOOM;
        zoomParams = {extent, zoom};
      } else {
        zoomParams = {extent};
      }
      extent && view.goTo(zoomParams, mapViewGoToOptions);
    } catch (err) {
      error(err);
    }
  }, 150), []);

  const onSelectedAsset = (item: CVSAssetType, coordinates: GenericObject) => {
    const asset = getLatestUpdate({
      list : assetsRef.current,
      item
    });
    onAssetSelected(asset || item, coordinates);
  };

  const {initializeSelection, onItemSelect, onItemDeselect} = useLayerSelection({
    onItemSelected : onSelectedAsset,
    onHoverItem    : onHoveredPanelAsset,
    layerId        : [ASSET_LAYER_ID, CLUSTER_LAYER_ID],
    view,
    clusterSelected,
    showGraphicPopup,
    currentPopupGraphic
  });

  /*if the user selects a tool from the side panel we need
  * to add that tool to the graphics list for this layer so that
  * the asset marker will display and we need to zoom into that point.
  */
  const validateSelectedTool = useCallback(features => {
    let toolDetails = null;
    if (toolDetailView) {
      if (disconnectedToolTagReport) {
        toolDetails = {
          ...selectedTool,
          tagReport        : disconnectedToolTagReport.tagReport,
          disconnectedTool : true
        };
      } else {
        toolDetails = {...selectedTool};
      }

      // moved to toolDetailView add the tool to the layer
      if (toolDetails.tagReport) {
        const {longitude, latitude} = toolDetails.tagReport || {};
        const constructedTool = {
          geometry : {
            coordinates : [longitude, latitude]
          },
          ...toolDetails
        };
        if (selectedTool?.architecture === TagArchitecture.SCI) {
          view.goTo({
            center : [longitude, latitude]
          }, mapViewGoToOptions);
        } else {
          features.push(constructedTool);
        }
      }
    }
    return {toolDetails};
  }, [toolDetailView, selectedTool, disconnectedToolTagReport]);

  /**
   * Start the setInterval function to get Assets updated based on the Inteval
   */
  const startAssetsAutoRefresh = useCallback(() => {
    const isTimerNotActive = !timerUpdateRef || !timerUpdateRef?.current;
    if (isTimerNotActive && !isLargeFleetMode && !findNearbyIsActiveRef.current &&
      Boolean(updateIntervalRef.current)) {
      startUpdate?.();
    }
  }, [isLargeFleetMode, layers]);

  /**
   * Stop the setInteval function
   */
  const stopAssetsAutoRefresh = useCallback(() => {
    stopUpdate?.();
  }, []);

  useEffect(() => {
    if (globalGroupId && globalGroupIdRef.current !== globalGroupId) {
      globalGroupIdRef.current   = globalGroupId;
      hasGroupChangedRef.current = true;
      removeHighlight(singleLayerRef?.current);
    }
  }, [globalGroupId]);

  useEffect(() => {
    viewRef.current = view;
  }, [view]);

  useEffect(() => {
    assetsLoadingRef.current = assetsLoading;
  }, [assetsLoading]);

  /**
   * Executing when leaving the tracking page
   */
  useEffect(() => {
    return function() {
      zoomWatchRef?.current?.remove();
      panWatchRef?.current?.remove();
      onAssetsUpdates && stopAssetsAutoRefresh();
      viewClickRef?.current?.remove('click', viewClickHandler);
    };
  }, []);

  useEffect(() => {
    assetsRef.current = assets;
    if (refreshLayerRef.current && !findNearbyIsActiveRef.current) {
      runClusterProccess();
      refreshLayerRef.current = false;
    }
  }, [assets]);

  useEffect(() => {
    assetsNormalFleetRef.current = assetsNormalFleet;
    assetsLargeFleetRef.current  = assetsLargeFleet;
    const assets = getTrackingAssets({
      assetsNormalFleet,
      assetsLargeFleet,
      viewZoom     : getMapZoomLevel(viewRef.current?.zoom, initialBasemap, isLargeFleetMode),
      isLargeFleet : isLargeFleetModeRef.current
    });
    assetsRef.current = assets;
    setAssets(assets);
  }, [assetsNormalFleet, assetsLargeFleet, isLargeFleetMode]);

  useEffect(() => {
    const isEnabled = isCurrentEnabled(layers);
    const {current: clusterLayer} = clusterLayerRef || {};
    const {current: singleLayer}  = singleLayerRef || {};
    clusterLayer && (clusterLayer.visible = isEnabled);
    singleLayer && (singleLayer.visible = isEnabled);
    layersRef.current = layers;
  }, [layers]);

  useEffect(() => {
    colorRef.current = color;
    if (clusterLayerRef.current && isCurrentEnabled(layers)) {
      clusterFeatures(null, isClusteringActive);
    }
    isClusteringActiveRef.current = isClusteringActive;
  }, [color, isClusteringActive]);

  useEffect(() => {
    !findNearbyIsActive && startAssetsAutoRefresh();
  }, [findNearbyIsActive]);

  /**
   * Executing the clusterFeature process when getting the Assets Updated
   */
  useEffect(() => {
    isLargeFleetModeRef.current = isLargeFleetMode;
    if (isLargeFleetMode && isCurrentEnabled(layers) && !currentPopupGraphic.current) {
      clusterFeatures(assets, isClusteringActiveRef.current);
    }
    if (isLargeFleetMode) {
      stopAssetsAutoRefresh();
    } else {
      startAssetsAutoRefresh();
    }
  }, [assets, isLargeFleetMode]);

  useEffect(() => {
    isPhoneRef.current = isPhone;
  }, [isPhone]);

  useEffect(() => {
    const {current}    = singleLayerRef;
    const id           = selectedAsset?.id;
    const shouldInvoke = Boolean(
      current &&
      isCurrentEnabled(layers) &&
      showAssetsList &&
      !searchResult &&
      id
    );
    if (shouldInvoke) {
      getFeatureByValue(current, 'id', id)
        .then(feature => feature && onClearSearchedAsset());
    }
    currentLocationRef.current = null;
  }, [searchResult]);

  // Apply single layer visibility
  const setSingleLayerVisibility = useCallback((visible?: boolean) => {
    if (singleLayerRef.current) {
      singleLayerRef.current.visible = visible ?? isCurrentEnabled(layersRef.current);
    }
  }, []);

  useEffect(() => {
    // Since we are only setting the viewReady to true this hook
    // wont run more than once, this is to help with the initial
    // load of the page.
    if (viewReady) {
      if (!zoomWatchRef?.current) {
        zoomWatchRef.current = view.watch('zoom', zoomLevel => {
          if (isClusteringActiveRef.current || isLargeFleetModeRef.current) {
            setSingleLayerVisibility(false);
          }
          onZoomChanged(zoomLevel);
        });
      }
      addGraphics(mapMarkers);
      startAssetsAutoRefresh();
    }
  }, [viewReady]);

  // Update the layers when the zoom changes
  const onZoomChanged = debounce((zoomLevel: number) => {
    if (isLargeFleetModeRef.current) {
      // Getting the view's level #
      const viewZoomLevel = getMapZoomLevel(
        Number(Math.floor(zoomLevel)),
        initialBasemap,
        isLargeFleetModeRef.current
      );
      // Getting the view's level based on server side ['countries', 'states', 'clusters']
      const currentZoomlevel = getAssetsAggregationLevel(assetsLargeFleetRef.current, viewZoomLevel);
      // Avoiding to update the graphics if the View's level is equal to 'clusters'
      if (currentZoomlevel !== DEFAULT_AGGREGATION_LEVEL) {
        const assets = getTrackingAssets({
          assetsNormalFleet : assetsNormalFleetRef.current,
          assetsLargeFleet  : assetsLargeFleetRef.current,
          isLargeFleet      : isLargeFleetModeRef.current,
          viewZoom          : viewZoomLevel
        });
        // Avoiding clearing graphics until getting updates
        if (assets?.length) {
          setAssets(assets);
        }
        const {visible} = singleLayerRef.current || {};
        // Setting the visibility of the Single Layer
        if (selectedAssetRef.current && !isNil(visible) && !visible) {
          singleLayerRef.current.queryFeatureCount()
            .then(count => count === 1 && setSingleLayerVisibility());
        }
      }
    } else {
      if (isClusteringActiveRef.current) {
        updateAssets(layersRef?.current, isClusteringActiveRef.current);
      }
    }
  }, 150);

  /**
   * Running the cluster process when selecting an asset in the view
   */
  useEffect(() => {
    if (isCurrentEnabled(layers)) {
      selectedAssetRef.current = getSelectedAsset(selectedAsset);
      if (clusterLayerRef.current) {
        clusterFeatures(null, isClusteringActive);
      }
      if (selectedAssetRef.current) {
        selectAsset(selectedAssetRef.current);
        setPreviousSelectedAsset(selectedAssetRef.current);
      } else {
        onItemDeselect();
      }
    }
  }, [isMapSelection, selectedAsset]);

  // Show the Map Popup on selecting the Asset markers.
  // Don't show default popup in Mobile view
  useEffect(() => {
    if (singleLayerRef.current && selectedPopupGraphic) {
      selectedPopupGraphic.attributes = getLatestUpdate({
        list : assetsRef.current,
        item : selectedPopupGraphic?.attributes
      });
    }
  }, [selectedPopupGraphic]);

  const shouldClosePopupAfterFindNearby = !isFindNearbyPopup && !assetDetailView;

  useEffect(() => {
    if (shouldClosePopupAfterFindNearby) {
      view.popup.close();
    }
    if (selectedAsset) {
      getFeatureByValue(singleLayerRef.current, 'id', selectedAsset.assetId)
        .then(feature => {
          if (feature && !isFindNearbyPopup) {
            findNearbyPopupActive.current = false;
          } else {
            findNearbyPopupActive.current = true;
          }
        });
    }
  }, [isFindNearbyPopup]);

  // If the user has manually panned the map, we need to bring the popup into
  // view to allow them to use the find nearby feature ...
  useEffect(() => {
    if (selectedAsset) {
      panToAsset();
      if (!isPhoneRef.current) {
        isFindNearbyPopupRef.current = true;
        getFeatureByValue(singleLayerRef.current, 'id', selectedAsset.assetId)
          .then(feature => {
            findNearbyPopupActive.current = false;
            isFindNearbyPopupRef.current  = false;
          });
      }
    }
  }, [isFindNearbyToggle]);

  // if the asset graphics layer has been initialized, we update the marker mode based
  // on the current zoom level of the map view ...
  useEffect(() => {
    if (clusterLayerRef.current) {
      updateAssets(layers, isClusteringActive);
    }
  }, [layers, renderMarkerWhenResume === true]);

  // if the asset graphics layer has been initialized, we update the marker mode based
  // on the current zoom level of the map view ...
  useEffect(() => {
    const {current: currentLayerRef}             = singleLayerRef;
    const {current: currentSuperClusterRef = []} = superclusterRef;
    const inToolDetailView     = toolDetailView && !isNil(selectedTool);
    const currentSelectedAsset = getSelectedAsset(selectedAsset);
    const assetInView          = inToolDetailView ? selectedTool : currentSelectedAsset;

    if (inToolDetailView) {
      return;
    }

    if (currentSelectedAsset) {
      isFullExtendRequired.current = false;
    }
    if (
      (currentLayerRef || currentSuperClusterRef) &&
      isCurrentEnabled(layers)
    ) {
      if (!isMapSelection && assetInView) {
        const {longitude, latitude} = assetInView || {};
        view.zoom = VIEW_ZOOM;
        view.goTo({center: [longitude, latitude]}, mapViewGoToOptions);
        view.popup?.close();
        currentLocationRef.current = null;
      }
    }
  }, [isMapSelection, selectedAsset, selectedTool, assetDetailView]);

  useEffect(() => {
    if (findNearbyResults?.length) {
      const points = getAssetPoints(findNearbyResults);
      const extent = zoomTo(points, EXTENT_FACTOR);
      extent && view.goTo(extent, mapViewGoToOptions).catch(err => error(err));
    }
  }, [findNearbyResults]);

  /**
   * @private
   */
  const shouldAddZoomLevel = useCallback((isLargeFleetMode: boolean, extent: __esri.Extent): boolean => {
    return isLargeFleetMode && shouldZoomTo(extent, view) &&
    view.zoom <= MAX_AGGREGATION_LEVEL;
  }, []);

  /**
   * @private
   *
   * Select an asset
   */
  const selectAsset = useCallback(selectedAsset => {
    const {id} = selectedAsset || {};
    if (id) {
      getFeatureByValue(singleLayerRef.current, 'id', id).then(feature => {
        const supercluster = superclusterRef.current?.points?.find(
          ({id: clusterId}) => {
            return clusterId === id;
          });
        const graphic = feature || supercluster;
        graphic && onItemSelect({
          item        : selectedAsset,
          graphic     : graphic,
          coordinates : null,
          omit        : true
        });
      });
    }
  }, []);

  const clusterFeatures = async(currentAssets?: CVSAssetType[], isClusteringActive?: boolean) => {
    if (clusterLayerRef.current) {
      const assets          = assetsRef?.current || [];
      const selectedAsset   = getSelectedAsset(selectedAssetRef.current);
      const isClientCluster = isClientSideCluster(isLargeFleetModeRef.current);
      zoomRef.current       = view.zoom;

      const {features} = await getAssetFeatures({
        assets               : (currentAssets || assets),
        currentSelectedAsset : selectedAsset,
        isClientCluster,
        isClusteringActive
      });

      validateSelectedTool(features);
      const singleAssets  = getSingleAssets(features);
      const clusterAssets = getClusterAssets(features);

      // Start - add Single Layer
      const params = {
        layer        : singleLayerRef.current,
        data         : singleAssets,
        create       : createAssetLayer,
        addUpdate    : addRemoveAssets,
        selectedItem : selectedAsset
      };
      addUpdateItems(params);
      // End - add Single Layer

      const {
        isLabellingActive,
        labelContent,
        defaultlabel
      } = labelRef?.current || {};
      const graphics = createMarkers({
        assets     : clusterAssets,
        Graphic    : graphicRef.current,
        mode       : getMarkerMode(view),
        markerOnly : !isNil(selectedPopupGraphic || currentPopupGraphic?.current),
        color      : colorRef.current,
        selectedAsset,
        isLabellingActive,
        labelContent,
        defaultlabel
      });
      removeAllGraphics();
      addGraphics(graphics);
      clusterLayerRef.current.visible = canDisplay(layersRef.current);
    }
  };

  /**
   * Provides a utility to get the (x, y) coordinates of the click event,
   * uses the utility method (onLayerClickEvent) to get the selected element,
   * and uses a callback to display the popup.
   */
  const viewClickHandler = useCallback(debounce(function({x, y}) {
    if (isCurrentEnabled(layersRef.current)) {
      onLayerClickEvent({
        layerId        : [ASSET_LAYER_ID, CLUSTER_LAYER_ID],
        callback       : showPopup,
        includeAny     : true,
        includeResults : true,
        view,
        x,
        y
      });
    }
  }, 200), [showPopup]);

  return null;
};

export default memo(AssetLayer);
